import { api } from './api'

function assertSuccess(body: unknown) {
  const b = body as { success?: boolean; message?: string; data?: unknown }
  if (!b?.success) {
    throw new Error(b?.message || 'Request failed')
  }
  return b.data
}

function apiError(err: unknown, fallback: string) {
  const e = err as { response?: { data?: { message?: string } }; message?: string }
  return new Error(e?.response?.data?.message || e?.message || fallback)
}

export type AdminWithdrawalRow = {
  id: number
  organisationId: number
  organisationName: string | null
  storeId: number
  storeName: string | null
  amount: string
  currency: string
  status: string
  createdAt: string
  processedAt: string | null
  adminNote: string | null
  stripeConnectReady?: boolean
  stripeTransferId?: string | null
  paystackRecipientReady?: boolean
  paystackTransferCode?: string | null
  transferReady?: boolean | null
  nextReadyAt?: string | null
  transferableAmount?: string | null
}

export type ListWithdrawalsResult = {
  items: AdminWithdrawalRow[]
  total: number
  page: number
  limit: number
}

export async function listWithdrawalRequests(params?: {
  page?: number
  limit?: number
  status?: 'pending' | 'approved' | 'paid' | 'rejected'
}): Promise<ListWithdrawalsResult> {
  try {
    const { data } = await api.get('/admin/withdrawal-requests', { params })
    return assertSuccess(data) as ListWithdrawalsResult
  } catch (e) {
    throw apiError(e, 'Failed to load withdrawal requests')
  }
}

export async function approveWithdrawalRequest(id: number, adminNote?: string | null) {
  try {
    const trimmed = adminNote?.trim()
    const body = trimmed ? { adminNote: trimmed } : {}
    const { data } = await api.patch(`/admin/withdrawal-requests/${id}/approve`, body)
    return assertSuccess(data) as { id: number; status: string }
  } catch (e) {
    throw apiError(e, 'Failed to approve withdrawal')
  }
}

export async function markWithdrawalDisbursed(id: number, adminNote?: string | null) {
  try {
    const trimmed = adminNote?.trim()
    const body = trimmed ? { adminNote: trimmed } : {}
    const { data } = await api.patch(`/admin/withdrawal-requests/${id}/mark-paid`, body)
    return assertSuccess(data) as { id: number; status: string }
  } catch (e) {
    throw apiError(e, 'Failed to mark withdrawal disbursed')
  }
}

export async function rejectWithdrawalRequest(id: number, reason: string) {
  try {
    const trimmed = reason.trim()
    if (!trimmed) {
      throw new Error('Rejection reason is required')
    }
    const { data } = await api.patch(`/admin/withdrawal-requests/${id}/reject`, { reason: trimmed })
    return assertSuccess(data) as { id: number; status: string }
  } catch (e) {
    throw apiError(e, 'Failed to reject withdrawal')
  }
}
